import { api } from './api';
import type { LanguageCode } from '@/i18n/languages';

export type ComposeRegister = 'first_contact_formal' | 'follow_up' | 'introduction' | 'friendly';
export type AuditScope = 'jsox' | 'sox' | 'gdpr' | 'appi' | 'general';

export interface ComposeRequest {
  brief: string;
  register: ComposeRegister;
  audience?: string;
  language: LanguageCode;
  corpusHint?: string;
  modelOverride?: string;
}

export interface ComposeResponse {
  draft: string;
  registerUsed: ComposeRegister;
  registerLabel: string;
  citedChunkIds: string[];
  model: string;
  costCents: number;
  inputTokens: number;
  outputTokens: number;
}

export interface BriefRequest {
  topic: string;
  audience: string;
  language: LanguageCode;
  length: 'short' | 'medium' | 'long';
}

export interface BriefResponse {
  topic: string;
  audience: string;
  language: string;
  outline: string[];
  body: string;
  citedChunkIds: string[];
  retrievedChunkIds: string[];
  model: string;
  costCents: number;
  inputTokens: number;
  outputTokens: number;
}

export interface CompareRequest {
  docAId?: string;
  docBId?: string;
  chunkIdsA?: string[];
  chunkIdsB?: string[];
  language: LanguageCode;
}

export interface CompareDelta {
  section: string;
  claim_a: string | null;
  claim_b: string | null;
  kind: 'added' | 'removed' | 'changed';
  confidence: number;
}

export interface CompareResponse {
  summary: string;
  deltas: CompareDelta[];
  citedChunkIds: string[];
  retrievedChunkIds: string[];
  model: string;
  costCents: number;
  inputTokens: number;
  outputTokens: number;
}

export interface AuditRequest {
  scope: AuditScope;
  question: string;
  language: LanguageCode;
}

export interface AuditResponse {
  scope: AuditScope;
  answer: string;
  policyRefs: string[];
  evidenceRefs: string[];
  citedChunkIds: string[];
  retrievedChunkIds: string[];
  model: string;
  costCents: number;
  inputTokens: number;
  outputTokens: number;
}

export interface WorkspaceDigest {
  generatedAt: string;
  stats: {
    newDocs: string[];
    newDocCount: number;
    failedDocs: number;
    pendingApprovals: number;
    recentQuestions: string[];
    quality: { totalAnswers: number; groundedRate: number | null };
  };
  brief: string;
}

export interface ResearchResponse {
  question: string;
  subQuestions: string[];
  answer: string;
  citedChunkIds: string[];
  retrievedChunkIds: string[];
  model: string;
  costCents: number;
  inputTokens: number;
  outputTokens: number;
}

export interface WorkflowRow {
  id: string;
  workspace_id: string;
  name: string;
  yaml: string;
  schedule: string | null;
  enabled: boolean;
  last_run_at: string | null;
  created_at: string;
}

export interface WorkflowRunResult {
  runId: string;
  name: string;
  steps: {
    kind: string;
    output: unknown;
    citedChunkIds?: string[];
    costCents?: number;
    model?: string;
    needsApproval?: boolean;
  }[];
  status: 'completed' | 'pending_approval' | 'failed';
  error?: string;
}

export interface WorkflowRunRow {
  id: string;
  workflow_id: string;
  status: 'running' | 'completed' | 'pending_approval' | 'failed';
  pending_step_index: number | null;
  started_at: string;
  ended_at: string | null;
}

export const agentsApi = {
  async compose(wsId: string, req: ComposeRequest): Promise<ComposeResponse> {
    const r = await api.post<ComposeResponse>(`/api/workspaces/${wsId}/compose`, req);
    return r.data;
  },
  async brief(wsId: string, req: BriefRequest): Promise<BriefResponse> {
    const r = await api.post<BriefResponse>(`/api/workspaces/${wsId}/brief`, req);
    return r.data;
  },
  async compare(wsId: string, req: CompareRequest): Promise<CompareResponse> {
    const r = await api.post<CompareResponse>(`/api/workspaces/${wsId}/compare`, req);
    return r.data;
  },
  async audit(wsId: string, req: AuditRequest): Promise<AuditResponse> {
    const r = await api.post<AuditResponse>(`/api/workspaces/${wsId}/audit`, req);
    return r.data;
  },
  async research(wsId: string, req: { question: string; language?: string }): Promise<ResearchResponse> {
    const r = await api.post<ResearchResponse>(`/api/workspaces/${wsId}/research`, req);
    return r.data;
  },
  async digest(wsId: string): Promise<WorkspaceDigest> {
    const r = await api.post<WorkspaceDigest>(`/api/workspaces/${wsId}/digest`);
    return r.data;
  },
  async listWorkflows(wsId: string): Promise<WorkflowRow[]> {
    const r = await api.get<WorkflowRow[]>(`/api/workspaces/${wsId}/workflows`);
    return r.data;
  },
  async createWorkflow(wsId: string, name: string, yaml: string, schedule?: string | null): Promise<WorkflowRow> {
    const r = await api.post<WorkflowRow>(`/api/workspaces/${wsId}/workflows`, { name, yaml, schedule });
    return r.data;
  },
  async runWorkflow(id: string): Promise<WorkflowRunResult> {
    const r = await api.post<WorkflowRunResult>(`/api/workflows/${id}/run`);
    return r.data;
  },
  async updateWorkflow(id: string, patch: { name?: string; yaml?: string; schedule?: string | null; enabled?: boolean }): Promise<WorkflowRow> {
    const r = await api.patch<WorkflowRow>(`/api/workflows/${id}`, patch);
    return r.data;
  },
  async deleteWorkflow(id: string): Promise<{ ok: boolean }> {
    const r = await api.delete<{ ok: boolean }>(`/api/workflows/${id}`);
    return r.data;
  },
  async listWorkflowRuns(id: string): Promise<WorkflowRunRow[]> {
    const r = await api.get<WorkflowRunRow[]>(`/api/workflows/${id}/runs`);
    return r.data;
  },
  async resumeWorkflowRun(runId: string): Promise<WorkflowRunResult> {
    const r = await api.post<WorkflowRunResult>(`/api/workflows/runs/${runId}/resume`);
    return r.data;
  },
};

export interface SignalRow {
  id: string;
  workspace_id: string;
  kind: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface SignalSubscriptionRow {
  id: string;
  workspace_id: string;
  user_id: string;
  kind: string;
  filter: Record<string, unknown>;
  delivery: string[];
  created_at: string;
}

export const signalsApi = {
  async list(wsId: string, kind?: string): Promise<SignalRow[]> {
    const r = await api.get<SignalRow[]>(`/api/workspaces/${wsId}/signals`, { params: { kind } });
    return r.data;
  },
  async listSubscriptions(wsId: string): Promise<SignalSubscriptionRow[]> {
    const r = await api.get<SignalSubscriptionRow[]>(`/api/workspaces/${wsId}/signal-subscriptions`);
    return r.data;
  },
  async subscribe(wsId: string, kind: string, filter?: Record<string, unknown>, delivery?: string[]): Promise<SignalSubscriptionRow> {
    const r = await api.post<SignalSubscriptionRow>(`/api/workspaces/${wsId}/signal-subscriptions`, { kind, filter, delivery });
    return r.data;
  },
  async unsubscribe(wsId: string, id: string): Promise<void> {
    await api.delete(`/api/workspaces/${wsId}/signal-subscriptions/${id}`);
  },
};
